import React, { useState, useEffect } from 'react'
import { Badge, Button, Card, Typography } from 'antd'
import { useHistory } from 'react-router-dom'
import { useManualQuery } from 'graphql-hooks'

import IntlMessages from '../../util/IntlMessages'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import YoutubeEmbed from '../youtube-embed'
import { DocsDrawerNavbar } from '../docs-drawer-content/DocsDrawerNavbar'
import { getWhatsNew as getWhatsNewQuery } from '../../graphql/custom-queries'
import { useAppSelector } from '../../store/hooks'
import { selectLocaleSlice } from '../../store/locale-slice'
import { rightActionBarTracker } from '../../containers/RightActionBar/services/rightActionBarTracking'

import { IWhatsNewComponent, IWhatsNewQuery } from '@vacationtracker/shared/types/whats-new'
import { FrontendUrls } from '../../types/urls'

const { Paragraph, Title } = Typography

interface WhatsNewDrawerContentProps {
  onClose?: () => void
}

export const WhatsNewDrawerContent: React.FC<WhatsNewDrawerContentProps> = ({ onClose }) => {
  const history = useHistory()
  const { locale } = useAppSelector(selectLocaleSlice)
  const [whatsNewData, setWhatsNewData] = useState<IWhatsNewComponent[]>([])
  const [loading, setLoading] = useState(true)

  const [getWhatsNewGqlQuery] = useManualQuery<IWhatsNewQuery, { limit: number }>(getWhatsNewQuery)

  useEffect(() => {
    const fetchWhatsNew = async () => {
      try {
        setLoading(true)
        const { data } = await getWhatsNewGqlQuery({ variables: { limit: 10 } })
        if (data?.getWhatsNew?.news) {
          setWhatsNewData(data.getWhatsNew.news)
        }
      } catch (error) {
        console.error('Error fetching whats new:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchWhatsNew()
  }, [getWhatsNewGqlQuery])

  const handleHomeNavigation = () => {
    if (onClose) {
      onClose()
    }
    history.push(FrontendUrls.dashboard)
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleLinkClick = (linkUrl: string) => {
    // Track changelog clicks
    if (linkUrl.toLowerCase().includes('changelog')) {
      rightActionBarTracker.trackChangelogClicked({
        source: 'whats_new_drawer',
      })
    }
  }

  return (
    <div className="whats-new-drawer-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navbar Header */}
      <DocsDrawerNavbar
        title={<IntlMessages id="components.whatsNew.whatsNew" />}
        isLoading={loading}
        showHomeButton={true}
        showOpenInNewTab={false}
        onHomeNavigation={handleHomeNavigation}
        onClose={handleClose}
      />

      {/* Content */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
          <IntlMessages id="app.loading" />
        </div>
      ) : !whatsNewData.length ? (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
          <IntlMessages id="components.whatsNew.noUpdates" />
        </div>
      ) : (
        <div className="help-drawer-inner" style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {whatsNewData.map((news: IWhatsNewComponent, index) => {
            return (
              <Badge.Ribbon
                key={`${news.title.replace(' ', '-')}-${index}`}
                text={<IntlMessages id="components.whatsNew.newMessage" />}
                style={news.unread ? {} : { display: 'none' }}
                placement="start"
              >
                <Card
                  className={news.unread && !news.imageUrl ? 'help-drawer-card unread' : 'help-drawer-card'}
                  cover={news.videoEmbedId ?
                    <YoutubeEmbed embedId={news.videoEmbedId} /> :
                    <img src={news.imageUrl} className='img-responsive' />
                  }
                >
                  <Title level={5}>{news.title}</Title>
                  <Paragraph style={{ whiteSpace: 'break-spaces' }}>
                    <div dangerouslySetInnerHTML={{__html: news.description}}></div>
                  </Paragraph>
                  {news.linkUrl &&
                <div>
                  <Button
                    type="default"
                    href={news.linkUrl}
                    target="_blank"
                    onClick={() => news.linkUrl && handleLinkClick(news.linkUrl)}
                  >
                    {news.linkText ? news.linkText : <IntlMessages id="app.learnMore" />}
                  </Button>
                  <span className="timestamp">
                    <FormattedDate
                      value={new Date(Number(news.timestamp)).toISOString()}
                      format="MMM DD, YYYY"
                      locale={locale.locale}
                    />
                  </span>
                </div>
                  }
                </Card>
              </Badge.Ribbon>
            )
          })}
        </div>
      )}
    </div>
  )
}
